// The LLM-models and Embedding-models screens are the same screen: a named
// provider + model + key, listed, created, tested and deleted. One component
// parameterised by kind keeps them from drifting apart, which matters because
// the *rules* differ subtly (an embedding model can't be repointed while
// knowledge bases depend on it) and those differences should be visible in one
// place rather than duplicated across two files.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Plus, XCircle } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import type { CatalogModel, LlmProvider, ModelInput, ModelTestResult } from "@/types/api";

/** The minimum a row needs to render; both view types satisfy it. */
export interface RegistryRow {
  id: number;
  name: string;
  slug: string;
  provider: string;
  model: string;
  key_hint: string | null;
  used_by_count: number;
  last_verified_at: string | null;
  last_error: string | null;
  dimensions?: number | null;
  /** The platform discarded this row's model — it will be refused at run time. */
  discarded?: boolean;
  discard_reason?: string | null;
  /** `false`: the model is not in the platform catalogue — also refused. */
  in_catalog?: boolean;
}

export interface ModelRegistryProps {
  title: string;
  description: string;
  /** What one row is called, lowercase and singular ("provider", "model").
   *  This component backs both the LLM-provider and embedding-model registries,
   *  so the word cannot be hardcoded to either. */
  noun?: string;
  /** Providers offered in the create form. Embeddings are OpenAI-only today. */
  providers: LlmProvider[];
  /** The models the platform offers for the chosen provider — the only ones a
   *  tenant may pick, since the platform catalogue is an allow-list. */
  catalogFor: (provider: LlmProvider) => CatalogModel[];
  /** What a tenant attaches these to. Used in the empty state's copy. */
  usedByNoun: string;
  /** What the ``used_by_count`` from the API actually counts. Distinct from
   *  ``usedByNoun`` because an LLM provider connection is referenced by agent versions *and*
   *  by the governance analyzer, so the count is not a count of versions. */
  usedByCountNoun: string;
  /** Embedding models expose a vector-width column. */
  showDimensions?: boolean;
  rows: RegistryRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
  queryKey: readonly unknown[];
  create: (input: ModelInput) => Promise<unknown>;
  /** Called after a successful create. Used to return the admin to whatever
   *  sent them here for a model (see lib/return-to). */
  onCreated?: () => void;
  remove: (slug: string) => Promise<unknown>;
  test: (slug: string) => Promise<ModelTestResult>;
}

export function ModelRegistry({
  title,
  description,
  noun = "model",
  providers,
  catalogFor,
  usedByNoun,
  usedByCountNoun,
  showDimensions = false,
  rows,
  isLoading,
  isError,
  error,
  refetch,
  queryKey,
  create,
  onCreated,
  remove,
  test,
}: ModelRegistryProps) {
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<RegistryRow | null>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey });

  const createMutation = useMutation({
    mutationFn: create,
    onSuccess: () => {
      toast.success("Model added.");
      setCreating(false);
      invalidate();
      onCreated?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: remove,
    onSuccess: () => {
      toast.success("Model deleted.");
      setPendingDelete(null);
      invalidate();
    },
    // The backend returns a 409 naming what still depends on it — surface that
    // verbatim rather than a generic failure.
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: test,
    onSuccess: (result) => {
      if (result.ok) {
        toast.success(
          result.dimensions ? `Works — ${result.dimensions} dimensions.` : "Credential works.",
        );
      } else {
        toast.error(result.error ?? "The credential did not work.");
      }
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div>
      <PageHeader
        title={title}
        description={description}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Add {noun}
          </Button>
        }
      />

      {isLoading ? (
        <Skeleton className="h-56 w-full" />
      ) : isError ? (
        <ErrorState error={error} onRetry={refetch} />
      ) : !rows?.length ? (
        <EmptyState
          title={`No ${noun}s configured yet`}
          hint={`Add one here and select it when creating ${usedByNoun} — the API key is entered once, not per ${usedByNoun.replace(/s$/, "")}.`}
          action={<Button onClick={() => setCreating(true)}>Add {noun}</Button>}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Provider</TH>
                  <TH>Model</TH>
                  {showDimensions ? <TH>Dimensions</TH> : null}
                  <TH>Key</TH>
                  <TH>In use</TH>
                  <TH>Status</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {rows.map((row) => (
                  <TR key={row.id}>
                    <TD>
                      <div className="font-medium">{row.name}</div>
                      <code className="text-xs text-muted-foreground">{row.slug}</code>
                    </TD>
                    <TD className="text-sm">{row.provider}</TD>
                    <TD>
                      <code className="text-xs">{row.model}</code>
                    </TD>
                    {showDimensions ? <TD className="text-sm">{row.dimensions ?? "—"}</TD> : null}
                    <TD>
                      <code className="text-xs text-muted-foreground">{row.key_hint ?? "—"}</code>
                    </TD>
                    <TD className="text-sm">
                      {row.used_by_count ? (
                        <Badge variant="secondary">
                          {row.used_by_count} {usedByCountNoun}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TD>
                    <TD>
                      {/* Outranks the credential status: a working key on a
                          discarded model still cannot run anything. */}
                      {row.discarded ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-destructive"
                          title={
                            row.discard_reason
                              ? `Discarded by the platform: ${row.discard_reason}`
                              : "Discarded by the platform administrator"
                          }
                        >
                          <XCircle className="h-3.5 w-3.5" /> model discarded
                        </span>
                      ) : row.in_catalog === false ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-destructive"
                          title="This model is not offered by the platform, so runs on it are refused."
                        >
                          <XCircle className="h-3.5 w-3.5" /> model not offered
                        </span>
                      ) : row.last_error ? (
                        <span
                          className="inline-flex items-center gap-1 text-xs text-destructive"
                          title={row.last_error}
                        >
                          <XCircle className="h-3.5 w-3.5" /> failed
                        </span>
                      ) : row.last_verified_at ? (
                        <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--success))]">
                          <CheckCircle2 className="h-3.5 w-3.5" /> verified
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">not tested</span>
                      )}
                    </TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={testMutation.isPending}
                          onClick={() => testMutation.mutate(row.slug)}
                        >
                          Test
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingDelete(row)}
                          title={
                            row.used_by_count
                              ? `In use by ${row.used_by_count} ${usedByCountNoun}`
                              : undefined
                          }
                        >
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CreateModelDialog
        open={creating}
        onOpenChange={setCreating}
        providers={providers}
        catalogFor={catalogFor}
        pending={createMutation.isPending}
        onSubmit={(input) => createMutation.mutate(input)}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name ?? noun}?`}
        description={
          pendingDelete?.used_by_count
            ? `This model is used by ${pendingDelete.used_by_count} ${usedByCountNoun}. Deleting it will be refused until they point somewhere else.`
            : "The stored API key is deleted with it. This cannot be undone."
        }
        confirmLabel="Delete"
        pending={deleteMutation.isPending}
        onConfirm={() => pendingDelete && deleteMutation.mutate(pendingDelete.slug)}
      />
    </div>
  );
}

// A strict dropdown by design: the platform catalogue is an allow-list, so a
// model the superadmin has not added cannot be used, and offering a free-text
// "other" field would only move the refusal to the submit button.
function CreateModelDialog({
  open,
  onOpenChange,
  providers,
  catalogFor,
  pending,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providers: LlmProvider[];
  catalogFor: (provider: LlmProvider) => CatalogModel[];
  pending: boolean;
  onSubmit: (input: ModelInput) => void;
}) {
  const [name, setName] = React.useState("");
  const [provider, setProvider] = React.useState<LlmProvider>(providers[0] ?? "openai");
  const [choice, setChoice] = React.useState("");
  const [apiKey, setApiKey] = React.useState("");

  const known = catalogFor(provider);
  const model = choice;

  React.useEffect(() => {
    if (open) {
      setName("");
      setProvider(providers[0] ?? "openai");
      setChoice("");
      setApiKey("");
    }
  }, [open, providers]);

  // Switching provider invalidates the selection — gpt-4o is not an Anthropic
  // model — so clear it rather than submitting a mismatched pair.
  React.useEffect(() => {
    setChoice("");
  }, [provider]);

  const valid = name.trim() && model.trim() && apiKey.trim().length >= 8;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a model</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            onSubmit({
              name: name.trim(),
              provider,
              model: model.trim(),
              api_key: apiKey.trim(),
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="model-name">Name</Label>
            <Input
              id="model-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="OpenAI production"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              How this appears in the picker when configuring an agent.
            </p>
          </div>

          {providers.length > 1 ? (
            <div className="space-y-2">
              <Label htmlFor="model-provider">Provider</Label>
              <select
                id="model-provider"
                value={provider}
                onChange={(e) => setProvider(e.target.value as LlmProvider)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {providers.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="model-id">Model</Label>
            <select
              id="model-id"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Select a model…</option>
              {known.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id}
                  {m.dimensions ? ` — ${m.dimensions}d` : ""}
                  {m.context_window ? ` — ${(m.context_window / 1000).toFixed(0)}k context` : ""}
                  {m.supports_tools === false ? " — no tool calls" : ""}
                  {m.input_per_1m != null && m.output_per_1m != null
                    ? ` — $${m.input_per_1m} / $${m.output_per_1m} per 1M`
                    : m.price_per_1m != null
                      ? ` — $${m.price_per_1m} per 1M`
                      : ""}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {known.length
                ? "Only models your platform offers can be used. Need another? Ask your platform administrator to add it."
                : `No ${provider} models are offered right now. Ask your platform administrator to add one.`}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model-key">API key</Label>
            <Input
              id="model-key"
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-…"
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              Stored encrypted and never shown again — only a masked hint.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || pending}>
              {pending ? "Adding…" : "Add model"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
